import { Form, Input, Modal, Select, Spin } from 'antd'
import { ChangeEvent, FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUser } from '../../../contexts'
import { useBlocktrainRules, useDestinations } from '../../../hooks'
import { useCreateBlocktrain } from '../../../hooks/blocktrain/useCreateBlocktrain'
import useTrainCompanies from '../../../hooks/trainCompany/useTrainCompanies'
import { BlocktrainBase } from '../../../types'
import idEntitySendSetter from '../../../utils/id-entity-send-setter'
import useOrigins from '../../../hooks/useOrigins'

interface BlocktrainModalProps {
  visible: boolean
  onCancel: () => void
}

const AddBlocktrainModal: FC<BlocktrainModalProps> = ({ visible, onCancel }) => {
  const { t } = useTranslation()
  const resetForm = (close?: boolean) => {
    form.resetFields()
    setNoteContent('')
    if (close) {
      onCancel()
    }
  }
  const origins = useOrigins()
  const blocktrainRules = useBlocktrainRules()
  const [form] = Form.useForm()
  const [noteContent, setNoteContent] = useState<string>('')
  const { createBlocktrain, createBlocktrainIsFetching } = useCreateBlocktrain({
    resetForm,
  })
  const { state: user } = useUser()
  const { allDestinationsOptions } = useDestinations()
  const { trainCompanies } = useTrainCompanies({})

  const handleSetNoteContent = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value)
  }

  const onValidationError = (e: any) => {
    console.error(e)
  }

  const handleCreateBlocktrain = async () => {
    await form
      .validateFields()
      .then(values => {
        const payload = idEntitySendSetter(values, [
          'origin',
          'destination',
          'status',
          'invoice',
          'createBy',
          'billingBatch',
          'trainCompany',
        ])
        createBlocktrain({
          ...payload,
          supplier: { id: user?.userDetail?.supplier?.id },
        } as unknown as BlocktrainBase)
      })
      .catch(onValidationError)
  }
  const handleCancel = () => {
    resetForm(true)
  }

  return (
    <Modal
      title={t('new_blocktrain')}
      open={visible}
      onCancel={handleCancel}
      cancelText={t('cancel')}
      className="entity-modal"
      okText={t('create')}
      onOk={handleCreateBlocktrain}
      okButtonProps={{ disabled: createBlocktrainIsFetching }}
    >
      <Form form={form} layout="horizontal">
        <Spin spinning={!!createBlocktrainIsFetching}>
          <Form.Item
            label={t('origin_yard')}
            name="origin"
            rules={[{ required: true, message: t('please_input_the_origin_yard') }]}
          >
            <Select placeholder={t('select_origin_yard')} options={origins} />
          </Form.Item>
          <Form.Item
            label={t('railcar_company')}
            name="trainCompany"
            rules={[{ required: true, message: t('please_input_train_company') }]}
          >
            <Select
              placeholder={t('select_train_company')}
              options={trainCompanies?.map(({ id, shortName }) => ({
                label: shortName,
                value: id,
              }))}
            />
          </Form.Item>
          <Form.Item
            label={t('destination')}
            name="destination"
            rules={[{ required: true, message: t('please_input_destination') }]}
          >
            <Select placeholder={t('select_destination')} options={allDestinationsOptions} />
          </Form.Item>
          <Form.Item
            label={t('railWaybill_number')}
            name="railCarWayBill"
            rules={blocktrainRules.rayCarWayBill}
          >
            <Input placeholder={t('input_railWaybill_number')} />
          </Form.Item>
          <Form.Item label={t('Comments')} name="note">
            <Input.TextArea
              onChange={handleSetNoteContent}
              value={noteContent}
              maxLength={1200}
              rows={3}
              allowClear
            />
          </Form.Item>
        </Spin>
      </Form>
    </Modal>
  )
}

export default AddBlocktrainModal
